// Twinden's policy pages (M42, D108), in plain English and written from
// what the system actually stores (docs/end-to-end-encryption.md and the
// server schema). Drafts for the owner to review; not legal advice.
import { SITE } from './site';

/** A paragraph or a list; `[text](/path)` and `[text](mailto:…)` become links. */
export type PolicyBlock = string | { list: string[] };

export interface Policy {
  title: string;
  intro: string;
  sections: Array<{ heading: string; body: PolicyBlock[] }>;
}

export type PolicySlug = 'privacy' | 'terms' | 'contact';

const { contact } = SITE;

export const POLICIES: Record<PolicySlug, Policy> = {
  privacy: {
    title: 'Privacy policy',
    intro: `${SITE.name} is a private place for families to talk. This page says what we store, what we can and can’t read, and who else is involved. It is run by ${SITE.legalName}.`,
    sections: [
      {
        heading: 'What we can’t read',
        body: [
          'Messages, photos, videos, voice notes and files sent in Twinden chats, and the contents of your family’s Lists, Documents and Links, are end-to-end encrypted. They are locked on your device before they reach us, and only the devices of the people in that chat or family hold the keys. We store them, but we can’t read them.',
          'Messages and family-app items created before end-to-end encryption arrived (October 2026) were stored the old way and remain readable to the server, and so are notifications for chats that aren’t encrypted yet.',
        ],
      },
      {
        heading: 'What we store and can see',
        body: [
          {
            list: [
              'Your account: your @handle and display name.',
              'Pictures: your profile picture, and your family’s and groups’ pictures, are end-to-end encrypted on your device. We keep them as data we can’t read. Pictures stored before this change are deleted after 14 days unless their owner’s app seals them first.',
              'Your passkeys’ public keys and device names. Your face, fingerprint or screen lock never leave your device; we never see them.',
              'Your devices’ public encryption keys, and encrypted copies of chat and family keys that only your devices can open.',
              'Which families and chats you’re in, and your role in each.',
              'About each message: who sent it, to which chat, when, its type, and whether it was edited, deleted, read, pinned or starred, plus reactions and who was mentioned (so we can notify them).',
              'Call history: who called whom, when and for how long. Calls are encrypted between the devices on the call; when a direct connection isn’t possible they pass through a relay still encrypted. We never have the keys to the audio or video.',
              'If you turn on notifications: the address your browser or phone gives us for delivering them, and your notification settings.',
              'A record of security events, such as signing in from a new device or changing someone’s role.',
            ],
          },
          'When you sign in, sign out or approve a sign-in, and when a sign-in attempt is refused because an old session token was presented, we log the time, the internet address (IP address) your device connected from, the chain of forwarding addresses the connection presented, the app version and device name it reported, and the session it belonged to. Each entry is kept for 180 days from the day it was made, as Indian law requires of us (the CERT-In directions of 2022 and the IT Rules 2021). We use it for nothing else and never show it in the app. Our hosting and network providers (Railway and Cloudflare) also keep short-lived technical logs to run and protect the service.',
          'When the app or the website hits an error, it reports what went wrong (the error, where in the app it happened, and the app version) to Twinden’s own server so we can fix it, with nothing that identifies you — no account, device, address or message — and nothing is sent to any third party; each kind of error is deleted 30 days after it was last seen.',
        ],
      },
      {
        heading: 'What we never do',
        body: [
          {
            list: [
              'No advertising, and no ad or analytics trackers in the app or on this site.',
              'We don’t sell or rent your information, or share it for marketing.',
              'We don’t read your encrypted content, and we couldn’t if we wanted to.',
            ],
          },
        ],
      },
      {
        heading: 'Who else is involved',
        body: [
          {
            list: [
              'Hosting and network: Twinden runs on Railway, which stores our database and files (encrypted content stays encrypted there). Connections to Twinden pass through Cloudflare’s network first, which protects the site and secures the connection from your device. Cloudflare sees your IP address and the technical details of each connection, never what you share, which is encrypted on your device before it leaves. It adds nothing to the app or the site, and it keeps short-lived technical logs to run and protect its network.',
              'Notifications: Apple, Google and your browser’s maker deliver notifications. For encrypted chats they carry no message content, only a signal that something is new; for chats that aren’t encrypted yet they include a short preview.',
              'Calls: to connect a call, phones ask Cloudflare for their public address, and when they can’t reach each other directly the call goes through a Cloudflare relay. Cloudflare sees the IP addresses and how much data passes, never the audio or video, which stay encrypted between the devices.',
              'Link previews: when you share a link, Twinden’s server fetches its title and picture so the link looks right. For encrypted chats, the preview isn’t kept on the server.',
              'Contributions: if you choose to chip in, Razorpay handles the payment under its own privacy policy. We receive your name, email, phone and the amount, never your card or bank details.',
            ],
          },
        ],
      },
      {
        heading: 'Requests for an invitation',
        body: [
          'If you ask for an invitation, we keep your name, email and the size of your family only so we can reply. Nobody else sees them. We delete a request 90 days after answering it, or sooner if you ask.',
        ],
      },
      {
        heading: 'Shape Twinden: ideas and votes',
        body: [
          'Shape Twinden (twinden.com/ideas) is a public board where signed-in people suggest ideas and vote for them. It is not a chat, so ideas and votes are not end-to-end encrypted: we store them as written, and anyone can read the ideas, signed in or not.',
          'Other families never see who posted an idea or who voted: every idea shows only as “from a Twinden family”. We keep who posted each idea and who voted, so each person votes once and posts are limited to three a day; only the deployment’s administrator can see who posted. Don’t put anything private in an idea.',
        ],
      },
      {
        heading: 'Reports and blocks',
        body: [
          'You can report someone in your family from a chat, and block someone so they can’t message or call you directly.',
          {
            list: [
              'A report keeps who sent it, who it’s about, the family, the reason you chose, your note if you wrote one, when it was sent, and the ids of the chat and message it was about. It never includes what was said: we can’t read your messages, and your device doesn’t send them with the report.',
              'Reports are seen by the family’s roots (not the person reported) and by Twinden’s administrator. A report about a root is seen only by Twinden’s administrator. Reports are deleted with the family, and kept if someone deletes their account (shown as from or about “Deleted account”).',
              'A block keeps only who blocked whom and when. The person you block isn’t told. Their messages in your direct chat are still stored, encrypted as always, but you aren’t notified and the chat stays hidden until you unblock them in Settings → Blocked people. Blocks don’t change family groups.',
            ],
          },
        ],
      },
      {
        heading: 'How long we keep things',
        body: [
          'We keep your information while your account exists. Deleting a message wipes its content.',
          'Each sign-in record is removed 180 days after it was made. When an account is deleted we keep its registration details — the @handle, the name, and when the account was made and deleted — for 180 days, as the IT Rules 2021 require, then delete them.',
        ],
      },
      {
        heading: 'Deleting your account',
        body: [
          'You can delete your account yourself at any time, in Settings → Account → Delete my account, after confirming with your passkey. It happens at once and can’t be undone.',
          {
            list: [
              'Deleted at once: your name, picture and birthday; your passkeys, devices and their encryption keys; your sessions; your notification addresses and settings; your stars; the relation names you gave others and those others gave you; and your pending invitations and requests to join.',
              'Messages you sent stay in your family’s chats, shown as from “Deleted account” (encrypted ones stay encrypted). Files you sent stay as long as the messages that hold them.',
              'You leave every family and chat, and their keys are changed so nothing new can be opened with anything you kept. If you were a family’s only root, its longest-standing admin (or, if there is none, member) becomes root, and the family sees a note saying so. A family with only you in it is deleted with everything in it.',
              'Your @handle is no longer shown and is never given to anyone else: we keep it on a list of retired handles, not linked to your account. We keep a bare record that the account existed, without your name or handle (after 180 days, see the next point), so old messages and the security log still make sense.',
              'For 180 days after the deletion we keep your registration details — your @handle, your name, and when the account was made and deleted — as the IT Rules 2021 require; only the deployment’s administrator can see them, to answer a lawful order, and after 180 days they are deleted. Your sign-in log keeps running out on its own schedule: each entry is removed 180 days after it was made, whether or not the account still exists.',
              'Copies in our backups age out within 30 days.',
            ],
          },
        ],
      },
      {
        heading: 'Deleting a family, and taking a copy first',
        body: [
          'A family’s root can delete the whole family, in Manage family → Danger zone → Delete family, after confirming with their passkey and typing the family’s name. It happens at once, for everyone, and can’t be undone.',
          {
            list: [
              'Deleted at once: every chat in the family with its messages, photos, videos, voice notes and files; the family’s Documents, Links and Lists; members’ stars and the relation names set in it; the family link, invitations and requests to join; and the family’s activity log. Files are removed from our storage straight after.',
              'Everyone in the family is told who deleted it. Their accounts stay, and so do their other families.',
              'We keep only a bare note that the family existed: its name, how many people were in it, who deleted it and when. Copies in our backups age out within 30 days.',
              'Before deleting, or at any time, a root or admin can download the family’s photos, documents, chat text, Links and Lists as one zip. It is put together on their own device, which holds the keys; we never see what’s in it. It covers the chats that person is in.',
            ],
          },
        ],
      },
      {
        heading: 'Children',
        body: [
          'Twinden is for people aged 13 and over. Families decide who joins them: a family’s admins are responsible for adding anyone under 18 and for making sure a parent or guardian agrees.',
        ],
      },
      {
        heading: 'Your choices',
        body: [
          'You can change your name and picture, remove devices, mute chats, switch notifications off, download a copy of your family’s photos and documents, and delete your account (or, as a root, your family) in the app. To see, correct or delete anything else we hold about you, write to us; we’ll answer within 30 days.',
        ],
      },
      {
        heading: 'Changes and contact',
        body: [
          `We’ll update this page when something changes and say so in the app if it matters. Questions: ${contact.email}. Complaints go to our Grievance Officer, ${contact.grievanceOfficer}, ${contact.grievanceEmail}, ${contact.address}; see [Contact](/contact).`,
        ],
      },
    ],
  },

  terms: {
    title: 'Terms of use',
    intro: `These terms are the agreement between you and ${SITE.legalName} for using ${SITE.name}. Using Twinden means you accept them.`,
    sections: [
      {
        heading: 'The service',
        body: [
          'Twinden lets families chat, call and keep shared lists, documents and links. It is free to use. We work to keep it running and secure, but it is provided as it is, without a promise that it will always be available or free of faults.',
        ],
      },
      {
        heading: 'Your account',
        body: [
          'You must be 13 or older to use Twinden, and if you’re under 18, a parent or guardian must agree. You join Twinden when someone in a family adds you. You sign in with passkeys on your own devices; keep those devices locked, and remove any you lose in Profile → My devices. You’re responsible for what is done from your account.',
        ],
      },
      {
        heading: 'Your content',
        body: [
          'What you send belongs to you. You give us permission only to store and deliver it, which for encrypted content means storing data we can’t read. Family admins can remove people from their family and its chats.',
        ],
      },
      {
        heading: 'Fair use',
        body: [
          {
            list: [
              'Don’t use Twinden for anything illegal, or to harass, threaten or harm anyone.',
              'Don’t share content you don’t have the right to share.',
              'Don’t try to break into, overload or disrupt the service or other people’s accounts.',
            ],
          },
          'If someone breaks these rules, report them from the chat (Report…) and block them if you like; your family’s roots and we see reports. You can also write to us.',
          'We may suspend an account or family that breaks these rules or puts the service or other families at risk.',
        ],
      },
      {
        heading: 'Contributions',
        body: [
          'Paying is voluntary. Contributions go towards Twinden’s costs (published on the pricing page), are one-time payments, and don’t buy extra features or change anyone’s access.',
        ],
      },
      {
        heading: 'Liability',
        body: [
          'To the extent the law allows, we aren’t liable for indirect losses, or for lost data beyond what we can reasonably prevent. Keep copies of anything you can’t afford to lose. Nothing here limits rights you have under law that can’t be limited.',
        ],
      },
      {
        heading: 'Changes, law and contact',
        body: [
          `We may update these terms; if a change matters, we’ll say so in the app. These terms are governed by the laws of India, and the courts of ${SITE.jurisdiction} have jurisdiction. Contact: ${contact.email}.`,
        ],
      },
    ],
  },

  contact: {
    title: 'Contact us',
    intro: `${SITE.name} is run by ${SITE.legalName}.`,
    sections: [
      {
        heading: 'Get in touch',
        body: [
          {
            list: [`Email: ${contact.email}`, `Address: ${contact.address}`],
          },
          'We usually reply within two working days.',
        ],
      },
      {
        heading: 'Complaints',
        body: [
          `Grievance Officer: ${contact.grievanceOfficer}, ${contact.grievanceEmail}, ${contact.address}. Complaints about the service, content, privacy or a contribution go to them. Under India’s IT Rules 2021, we acknowledge a complaint within 24 hours and resolve it within 15 days.`,
        ],
      },
      {
        heading: 'Starting Twinden for your family',
        body: [
          `Twinden is invite-only. If someone in your family already uses it, ask them to add you. To start a space for your family, [request an invitation](/request-invite), or [email us](mailto:${contact.email}).`,
        ],
      },
    ],
  },
};
